import React, { useEffect } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { RequestSchemaStatus, RequestSchemaType } from '@liscio/api';
import { WorkflowRequestStatus } from '@liscio/api/graphql';
import {
  RequestSectionList,
  RequestDetailsPanel,
  RequestSubDetailsPanel,
  useRequestStore,
  useUpdateWorkflowRequest,
  useRequestNavigation,
} from '@liscio/common';
import { Button, Divider, Stack } from '@liscio/ui';

import { TaxDeliveryRequestMainPanel } from './RequestDesktopDispatchView/components';
import { MarkCompleteButton } from '../components/MarkCompleteButton/MarkCompleteButton';
import { useRequestDesktopView } from '../hooks/useRequestDesktopView';
import RequestContextMenu from 'modules/requests/components/RequestContextMenu/RequestContextMenu';
import { RequestEdit } from 'modules/requests/components/RequestEdit/RequestEdit';

const DefaultRequestViewDesktopV5 = React.lazy(() =>
  import('@liscio/common').then((module) => ({
    default: module.DefaultRequestViewDesktopV5,
  }))
);

type RequestDesktopFirmViewProps = {
  id: string;
  status: RequestSchemaStatus;
};

export function RequestDesktopFirmView({
  id,
  status,
}: RequestDesktopFirmViewProps) {
  const { visibleSections, setDisableEdit, disableEdit, request, setRequest } =
    useRequestStore();
  const { mutate: updateWorkflowRequest } = useUpdateWorkflowRequest();
  const { workflowOpened: workflowOpenedFlag } = useFlags();
  const { isTaxDeliveryRequest } = useRequestNavigation();

  // set open if assignee is viewing the request
  useEffect(() => {
    if (status === RequestSchemaStatus.pending && workflowOpenedFlag) {
      updateWorkflowRequest({
        workflowRequestId: id,
        status: WorkflowRequestStatus.Open,
      });
    }
  }, [id, status, updateWorkflowRequest, workflowOpenedFlag]);

  const {
    rightPanelOpen: editDrawerOpen,
    leftPanelOpen: detailPanelOpen,
    onOpenLeftPanel,
    onCloseLeftPanel,
    onOpenRightPanel,
    onCloseRightPanel,
    handleOnSectionClick,
  } = useRequestDesktopView({ id });

  const canEditResponses =
    request &&
    (request.status === RequestSchemaStatus.open ||
      request.status === RequestSchemaStatus.pending) &&
    (request.type === RequestSchemaType.standard ||
      request.type === RequestSchemaType.tax_organizer);

  const canMarkComplete =
    request &&
    (request.status === RequestSchemaStatus.open ||
      request.status === RequestSchemaStatus.pending);

  // This function is used to toggle the edit mode for the request.
  const handleChangeResponses = () => {
    const newDisableEditValue = !disableEdit;
    setDisableEdit(newDisableEditValue);

    if (request) {
      // Create a shallow copy of the request with the updated isEditable value
      // isEditable should be the OPPOSITE of disableEdit
      const updatedRequest = {
        ...request,
        isEditable: !newDisableEditValue,
      };

      setRequest(updatedRequest);
    }
  };

  return (
    <DefaultRequestViewDesktopV5
      slotProps={{
        RequestBody: {
          RequestLeftPanel: {
            open: detailPanelOpen,
            toggle: false,
            onOpen: onOpenLeftPanel,
            onClose: onCloseLeftPanel,
          },
          RequestRightPanel: {
            open: isTaxDeliveryRequest ? true : editDrawerOpen,
            toggle: !isTaxDeliveryRequest,
            onOpen: onOpenRightPanel,
            onClose: onCloseRightPanel,
          },
        },
      }}
      slots={{
        RequestMainPanel: isTaxDeliveryRequest ? (
          <TaxDeliveryRequestMainPanel disabled={disableEdit} />
        ) : undefined,
        RequestLeftPanel: !isTaxDeliveryRequest ? (
          <>
            <RequestSubDetailsPanel />
            <RequestSectionList
              sections={visibleSections}
              onSectionClick={handleOnSectionClick}
            />
          </>
        ) : undefined,
        RequestRightPanel: (
          <Stack>
            <RequestDetailsPanel />
            <Divider />
            <RequestEdit
              open={true}
              row={{ uuid: id }}
              width={'40vw'}
              maxWidth={'40rem'}
              onClose={onOpenRightPanel}
            />
          </Stack>
        ),
        RequestHeaderActions: (
          <>
            {canEditResponses && (
              <Button variant="outlined" onClick={handleChangeResponses}>
                {disableEdit ? 'Change Responses' : 'Save Responses'}
              </Button>
            )}
            {canMarkComplete && <MarkCompleteButton requestId={id} />}
            <RequestContextMenu />
          </>
        ),
      }}
    />
  );
}
